import { useEffect, useRef, useState, useMemo, useCallback } from "react";
import ForceGraph3D, { type ForceGraph3DInstance } from "3d-force-graph";
import * as THREE from "three";
import type { GraphData, GraphNode, GraphEdge } from "../types/graph";
import type { FilterState } from "../types/filters";
import { filterGraph } from "./filterGraph";
import { getNodeColor, type ColorMode } from "./NodeColorizer";
import { getEdgeStyle } from "./EdgeStyler";
import { LODManager, DEFAULT_LOD_CONFIG } from "./LODManager";

export interface ForceGraph3DWrapperProps {
  graphData: GraphData;
  filters: FilterState;
  selectedNodeId: string | null;
  searchHighlights: Set<string>;
  colorMode: "kind" | "community";
  onNodeClick: (node: GraphNode) => void;
  onNodeDoubleClick: (node: GraphNode) => void;
  onBackgroundClick: () => void;
  onEdgeClick?: (edge: GraphEdge) => void;
}

const KIND_BASE_SIZE: Record<string, number> = {
  Function: 4,
  Class: 6,
  Method: 3,
  Interface: 5,
  Enum: 4,
  Type: 3,
  Module: 7,
  File: 5,
  Package: 8,
  Folder: 6,
};

const DBLCLICK_MS = 300;

function detectWebGL2(): boolean {
  try {
    const canvas = document.createElement("canvas");
    const gl = canvas.getContext("webgl2");
    return gl !== null;
  } catch {
    return false;
  }
}

function createTextSprite(text: string, color: string): THREE.Sprite {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d")!;
  canvas.width = 256;
  canvas.height = 64;
  ctx.font = "24px sans-serif";
  ctx.fillStyle = color;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(text, canvas.width / 2, canvas.height / 2, canvas.width - 8);
  const texture = new THREE.CanvasTexture(canvas);
  texture.needsUpdate = true;
  const spriteMaterial = new THREE.SpriteMaterial({ map: texture, transparent: true, depthWrite: false });
  const sprite = new THREE.Sprite(spriteMaterial);
  sprite.scale.set(20, 5, 1);
  return sprite;
}

type GraphNodePositioned = GraphNode & { x?: number; y?: number; z?: number; vx?: number; vy?: number; vz?: number; community_id?: number | null };

export function ForceGraph3DWrapper({
  graphData,
  filters,
  selectedNodeId,
  searchHighlights,
  colorMode,
  onNodeClick,
  onNodeDoubleClick,
  onBackgroundClick,
  onEdgeClick,
}: ForceGraph3DWrapperProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const graphRef = useRef<ForceGraph3DInstance | null>(null);
  const [webglSupported, setWebglSupported] = useState<boolean>(true);

  // Community hull meshes (additive — added directly to scene)
  const communityHullsRef = useRef<THREE.Mesh[]>([]);
  // Label sprites for selected/highlighted nodes
  const labelSpritesRef = useRef<Map<string, THREE.Sprite>>(new Map());
  // rAF handle for label-sprite position sync loop
  const rafHandleRef = useRef<number>(0);
  // Double-click timing
  const lastClickTimeRef = useRef<{ id: string; t: number }>({ id: "", t: 0 });

  useEffect(() => {
    setWebglSupported(detectWebGL2());
  }, []);

  const filteredData = useMemo(() => filterGraph(graphData, filters), [graphData, filters]);

  // ---------------------------------------------------------------------------
  // rAF loop: keep label sprites positioned over their nodes each frame
  // ---------------------------------------------------------------------------
  const syncLabelPositions = useCallback(() => {
    const graph = graphRef.current;
    if (graph && labelSpritesRef.current.size > 0) {
      const { nodes } = graph.graphData() as unknown as { nodes: GraphNodePositioned[] };
      const nodeById = new Map(nodes.map((n) => [n.id, n]));
      for (const [nodeId, sprite] of labelSpritesRef.current.entries()) {
        const n = nodeById.get(nodeId);
        if (n && n.x !== undefined) {
          sprite.position.set(n.x, (n.y ?? 0) + 12, n.z ?? 0);
        }
      }
    }
    rafHandleRef.current = requestAnimationFrame(syncLabelPositions);
  }, []);

  // ---------------------------------------------------------------------------
  // Community bounding-sphere hulls at 15% opacity (built after simulation stops)
  // ---------------------------------------------------------------------------
  const buildCommunityHulls = useCallback(() => {
    const graph = graphRef.current;
    if (!graph) return;
    const scene = graph.scene();

    for (const mesh of communityHullsRef.current) {
      scene.remove(mesh);
      mesh.geometry.dispose();
      (mesh.material as THREE.Material).dispose();
    }
    communityHullsRef.current = [];

    const { nodes } = graph.graphData() as unknown as { nodes: GraphNodePositioned[] };
    const byCommunity = new Map<string, Array<{ x: number; y: number; z: number }>>();
    for (const node of nodes) {
      if (node.x === undefined) continue;
      const cid = String(node.community_id ?? "default");
      if (!byCommunity.has(cid)) byCommunity.set(cid, []);
      byCommunity.get(cid)!.push({ x: node.x, y: node.y ?? 0, z: node.z ?? 0 });
    }

    let idx = 0;
    for (const [, positions] of byCommunity.entries()) {
      if (positions.length < 2) { idx++; continue; }
      let cx = 0, cy = 0, cz = 0;
      for (const p of positions) { cx += p.x; cy += p.y; cz += p.z; }
      cx /= positions.length; cy /= positions.length; cz /= positions.length;
      let radius = 0;
      for (const p of positions) {
        const dx = p.x - cx, dy = p.y - cy, dz = p.z - cz;
        radius = Math.max(radius, Math.sqrt(dx * dx + dy * dy + dz * dz));
      }
      radius = Math.max(radius + 15, 25);
      const hue = ((idx * 137.508) % 360) / 360;
      const color = new THREE.Color().setHSL(hue, 0.7, 0.5);
      const geometry = new THREE.SphereGeometry(radius, 16, 12);
      const material = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.15, side: THREE.BackSide, depthWrite: false });
      const mesh = new THREE.Mesh(geometry, material);
      mesh.position.set(cx, cy, cz);
      scene.add(mesh);
      communityHullsRef.current.push(mesh);
      idx++;
    }
  }, []);

  // ---------------------------------------------------------------------------
  // Label sprites for selected + highlighted nodes
  // ---------------------------------------------------------------------------
  const syncLabelSprites = useCallback(() => {
    const graph = graphRef.current;
    if (!graph) return;
    const scene = graph.scene();

    for (const sprite of labelSpritesRef.current.values()) {
      scene.remove(sprite);
      (sprite.material as THREE.SpriteMaterial).map?.dispose();
      (sprite.material as THREE.SpriteMaterial).dispose();
    }
    labelSpritesRef.current.clear();

    const { nodes } = graph.graphData() as unknown as { nodes: GraphNodePositioned[] };
    for (const n of nodes) {
      if (n.id !== selectedNodeId && !searchHighlights.has(n.id)) continue;
      const color = getNodeColor(n, colorMode as ColorMode);
      const sprite = createTextSprite(n.name, color);
      sprite.position.set(n.x ?? 0, (n.y ?? 0) + 12, n.z ?? 0);
      scene.add(sprite);
      labelSpritesRef.current.set(n.id, sprite);
    }
  }, [selectedNodeId, searchHighlights, colorMode]);

  // ---------------------------------------------------------------------------
  // Community centroid attraction + inter-cluster repulsion (d3 force)
  // ---------------------------------------------------------------------------
  const addCommunityForce = useCallback(() => {
    const graph = graphRef.current;
    if (!graph) return;

    graph.d3Force("communityAttraction", (alpha: number) => {
      const { nodes } = graph.graphData() as unknown as { nodes: GraphNodePositioned[] };
      const centroids: Record<string, { x: number; y: number; z: number; count: number }> = {};
      for (const n of nodes) {
        const cid = String(n.community_id ?? "default");
        if (!centroids[cid]) centroids[cid] = { x: 0, y: 0, z: 0, count: 0 };
        centroids[cid].x += n.x ?? 0;
        centroids[cid].y += n.y ?? 0;
        centroids[cid].z += n.z ?? 0;
        centroids[cid].count++;
      }
      for (const cid of Object.keys(centroids)) {
        const c = centroids[cid];
        c.x /= c.count; c.y /= c.count; c.z /= c.count;
      }
      for (const n of nodes) {
        const cid = String(n.community_id ?? "default");
        const centroid = centroids[cid];
        if (!centroid) continue;
        n.vx = (n.vx ?? 0) + (centroid.x - (n.x ?? 0)) * 0.06 * alpha;
        n.vy = (n.vy ?? 0) + (centroid.y - (n.y ?? 0)) * 0.06 * alpha;
        n.vz = (n.vz ?? 0) + (centroid.z - (n.z ?? 0)) * 0.06 * alpha;
      }
      const cids = Object.keys(centroids);
      for (let i = 0; i < cids.length; i++) {
        for (let j = i + 1; j < cids.length; j++) {
          const a = centroids[cids[i]];
          const b = centroids[cids[j]];
          const dx = b.x - a.x, dy = b.y - a.y, dz = b.z - a.z;
          const dist = Math.sqrt(dx * dx + dy * dy + dz * dz) || 1;
          const repulse = (250 / (dist * dist)) * alpha;
          for (const n of nodes) {
            const ncid = String(n.community_id ?? "default");
            const sign = ncid === cids[i] ? -1 : ncid === cids[j] ? 1 : 0;
            if (sign === 0) continue;
            n.vx = (n.vx ?? 0) + sign * (dx / dist) * repulse;
            n.vy = (n.vy ?? 0) + sign * (dy / dist) * repulse;
            n.vz = (n.vz ?? 0) + sign * (dz / dist) * repulse;
          }
        }
      }
    });
  }, []);

  // ---------------------------------------------------------------------------
  // Initialize force graph once
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!containerRef.current || !webglSupported) return;

    const graph = new ForceGraph3D(containerRef.current);

    graph
      .backgroundColor("#ffffff")
      .showNavInfo(false)
      .enableNodeDrag(true)
      .enableNavigationControls(true)
      .linkDirectionalArrowLength(4)
      .linkDirectionalArrowRelPos(0.9)
      .linkDirectionalParticles(2)
      .linkDirectionalParticleWidth(1.5)
      .linkDirectionalParticleSpeed(0.005);

    // Use built-in node rendering (visible immediately, no delay waiting for onEngineStop)
    graph.nodeColor((node) => getNodeColor(node as unknown as GraphNode, colorMode as ColorMode));
    graph.nodeVal((node) => {
      const graphNode = node as unknown as GraphNode;
      return KIND_BASE_SIZE[graphNode.kind ?? ""] ?? 4;
    });
    graph.nodeLabel((node) => {
      const graphNode = node as unknown as GraphNode;
      return `${graphNode.name} (${graphNode.kind})`;
    });

    // Double-click detection (300 ms window)
    graph.onNodeClick((node) => {
      const graphNode = node as unknown as GraphNode;
      const now = Date.now();
      const last = lastClickTimeRef.current;
      if (last.id === graphNode.id && now - last.t < DBLCLICK_MS) {
        onNodeDoubleClick(graphNode);
        lastClickTimeRef.current = { id: "", t: 0 };
      } else {
        lastClickTimeRef.current = { id: graphNode.id, t: now };
        onNodeClick(graphNode);
      }
    });

    graph.onNodeRightClick((node) => {
      onNodeDoubleClick(node as unknown as GraphNode);
    });

    graph.onBackgroundClick(() => {
      onBackgroundClick();
    });

    graph.onLinkClick((link) => {
      if (!onEdgeClick) return;
      const edge = link as unknown as GraphEdge;
      // After force simulation, source/target may be node objects — normalise to IDs
      const srcId = typeof edge.source === "string" ? edge.source : (edge.source as unknown as GraphNode).id;
      const tgtId = typeof edge.target === "string" ? edge.target : (edge.target as unknown as GraphNode).id;
      onEdgeClick({ ...edge, source: srcId, target: tgtId });
    });

    // Community hulls are built once the simulation has settled positions
    graph.onEngineStop(() => {
      buildCommunityHulls();
    });

    graphRef.current = graph;

    addCommunityForce();

    // Start rAF loop to keep label sprites over their nodes
    rafHandleRef.current = requestAnimationFrame(syncLabelPositions);

    return () => {
      cancelAnimationFrame(rafHandleRef.current);
      const scene = graphRef.current?.scene();
      if (scene) {
        for (const mesh of communityHullsRef.current) {
          scene.remove(mesh);
          mesh.geometry.dispose();
          (mesh.material as THREE.Material).dispose();
        }
        for (const sprite of labelSpritesRef.current.values()) {
          scene.remove(sprite);
          (sprite.material as THREE.SpriteMaterial).map?.dispose();
          (sprite.material as THREE.SpriteMaterial).dispose();
        }
      }
      communityHullsRef.current = [];
      labelSpritesRef.current.clear();
      if (graphRef.current) {
        graphRef.current._destructor();
        graphRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [webglSupported]);

  // ---------------------------------------------------------------------------
  // Update node colors when colorMode changes
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const graph = graphRef.current;
    if (!graph) return;
    graph.nodeColor((node) => getNodeColor(node as unknown as GraphNode, colorMode as ColorMode));
  }, [colorMode]);

  // ---------------------------------------------------------------------------
  // Update graph data
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const graph = graphRef.current;
    if (!graph) return;

    const nodes =
      filteredData.nodes.length > DEFAULT_LOD_CONFIG.progressiveLoadLimit
        ? LODManager.getInitialNodeSubset(filteredData.nodes, DEFAULT_LOD_CONFIG.progressiveLoadLimit)
        : filteredData.nodes;

    const visibleNodeIds = new Set(nodes.map((n) => n.id));
    const edges = filteredData.edges.filter(
      (e) => visibleNodeIds.has(e.source) && visibleNodeIds.has(e.target)
    );

    graph.graphData({
      nodes: nodes.map((n) => ({ ...n })),
      links: edges.map((e) => ({ ...e, source: e.source, target: e.target })),
    });
  }, [filteredData]);

  // ---------------------------------------------------------------------------
  // Edge rendering
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const graph = graphRef.current;
    if (!graph) return;

    graph.linkColor((link) => getEdgeStyle((link as unknown as GraphEdge).type).color);
    graph.linkWidth((link) => getEdgeStyle((link as unknown as GraphEdge).type).width);
    graph.linkOpacity(0.6);

    graph.linkVisibility((link) => {
      const { links } = graph.graphData() as unknown as { links: unknown[] };
      // Skip LOD entirely for small graphs — only throttle when edge count is very large
      if (links.length <= 200) return true;
      const camera = graph.camera();
      if (!camera) return true;
      const cameraPosition = camera.position;
      // After force simulation, source/target may be node objects — resolve to Vector3
      const resolvePos = (nodeOrId: unknown): THREE.Vector3 | undefined => {
        if (nodeOrId && typeof nodeOrId === "object") {
          const n = nodeOrId as GraphNodePositioned;
          if (n.x !== undefined) return new THREE.Vector3(n.x, n.y ?? 0, n.z ?? 0);
        }
        if (typeof nodeOrId === "string") {
          const data = graph.graphData() as unknown as { nodes: Array<GraphNodePositioned> };
          const n = data.nodes.find((nd) => nd.id === nodeOrId);
          if (n && n.x !== undefined) return new THREE.Vector3(n.x, n.y ?? 0, n.z ?? 0);
        }
        return undefined;
      };
      const edge = link as unknown as GraphEdge;
      const srcPos = resolvePos(edge.source);
      const tgtPos = resolvePos(edge.target);
      if (!srcPos && !tgtPos) return true; // can't resolve — show rather than hide
      const threshold = DEFAULT_LOD_CONFIG.edgeDistanceThreshold;
      if (srcPos && cameraPosition.distanceTo(srcPos) < threshold) return true;
      if (tgtPos && cameraPosition.distanceTo(tgtPos) < threshold) return true;
      return false;
    });
  }, []);

  // ---------------------------------------------------------------------------
  // Sync label sprites when selection/highlights change
  // ---------------------------------------------------------------------------
  useEffect(() => {
    syncLabelSprites();
  }, [syncLabelSprites]);

  if (!webglSupported) {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", minHeight: 400, backgroundColor: "#ffffff", color: "#150027", padding: 32, textAlign: "center" }}>
        <h2 style={{ marginBottom: 16, color: "#f85149" }}>WebGL 2.0 Required</h2>
        <p style={{ maxWidth: 480, lineHeight: 1.6, color: "#7d8590" }}>
          The 3D graph visualization requires WebGL 2.0 support, which is not available in your browser. Please use a modern browser with WebGL 2.0 support.
        </p>
      </div>
    );
  }

  return (
    <div ref={containerRef} style={{ width: "100%", height: "100%", minHeight: 400 }} />
  );
}

export default ForceGraph3DWrapper;
